import { useAuthStore } from "../store/auth-store";
import type {
  AdminBlogPost, BlogPostDetail, AdminBrand, AdminCategory, AdminContactMessage, AdminCoupon,
  AdminProductListItem, AdminReview, AdminShippingMethod, ApiProblem, AuthToken,
  ContactStatus, OrderDetail, OrderListItem, OrderStatus, PagedResult, ProductDetail,
  ProductStatus, ReviewStatus, SaveBlogPostRequest, SaveBrandRequest,
  SaveCategoryRequest, SaveCouponRequest, SaveFaqRequest, SaveProductRequest,
  SaveShippingMethodRequest, AdminFaq, AccountProfile, TwoFactorStatus, TwoFactorSetup, TwoFactorEnabled, ProductGender, AdminDashboardSummary, AdminOrderSummary, AdminReviewSummary, AdminBlogSummary, AdminContactSummary, BlogPostStatus, AccountSession, AdminUserListItem, AdminUserDetail, CreateAdminUserRequest, UpdateAdminUserRequest, AdminAccessOverview, AdminStaffListItem, CreateAdminStaffRequest, UpdateAdminStaffRequest,
} from "../types/api";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/$/, "");
const ASSET_BASE = (import.meta.env.VITE_ASSET_BASE_URL || "").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  problem?: ApiProblem;
  constructor(message: string, status: number, problem?: ApiProblem) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

export function assetUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return `${ASSET_BASE}${value.startsWith("/") ? "" : "/"}${value}`;
}

export function queryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  const result = search.toString();
  return result ? `?${result}` : "";
}

let refreshPromise: Promise<AuthToken | null> | null = null;

async function refreshSession(): Promise<AuthToken | null> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;

  refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) throw new Error("refresh failed");
      const session = (await response.json()) as AuthToken;
      useAuthStore.getState().setSession(session);
      return session;
    })
    .catch(() => {
      useAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
}

async function parseError(response: Response): Promise<ApiError> {
  let problem: ApiProblem | undefined;
  try { problem = (await response.json()) as ApiProblem; } catch { problem = undefined; }
  const validation = problem?.errors ? Object.values(problem.errors).flat()[0] : undefined;
  return new ApiError(validation || problem?.detail || problem?.title || `خطای ${response.status}`, response.status, problem);
}

export async function apiRequest<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData) && init.body !== undefined) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json");

  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (response.status === 401 && retry && useAuthStore.getState().refreshToken) {
    const refreshed = await refreshSession();
    if (refreshed) return apiRequest<T>(path, init, false);
  }
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });

export const authApi = {
  login: (identifier: string, password: string, rememberMe = true, twoFactorCode?: string) =>
    apiRequest<AuthToken>("/auth/login", json("POST", { identifier, password, rememberMe, twoFactorCode: twoFactorCode || null })),
  revoke: (refreshToken: string) => apiRequest<void>("/auth/revoke", json("POST", { refreshToken })),
};

export const adminApi = {
  dashboard: {
    summary: () => apiRequest<AdminDashboardSummary>("/admin/dashboard/summary"),
  },
  products: {
    list: (params: { search?: string; status?: ProductStatus | ""; brandId?: string; categoryId?: string; gender?: ProductGender | ""; stockState?: "" | "in" | "low" | "out"; featured?: boolean | ""; minPrice?: number; maxPrice?: number; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<AdminProductListItem>>(`/admin/products${queryString(params)}`),
    trash: (params: { search?: string; sort?: string; page?: number; pageSize?: number }) => apiRequest<PagedResult<AdminProductListItem>>(`/admin/products/trash${queryString(params)}`),
    detail: (id: string) => apiRequest<ProductDetail>(`/admin/products/${id}`),
    create: (body: SaveProductRequest) => apiRequest<ProductDetail>("/admin/products", json("POST", body)),
    update: (id: string, body: SaveProductRequest) => apiRequest<ProductDetail>(`/admin/products/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/products/${id}`, { method: "DELETE" }),
    restore: (id: string) => apiRequest<void>(`/admin/products/${id}/restore`, { method: "POST" }),
    removePermanent: (id: string) => apiRequest<void>(`/admin/products/${id}/permanent`, { method: "DELETE" }),
  },
  brands: {
    list: () => apiRequest<AdminBrand[]>("/admin/brands"),
    create: (body: SaveBrandRequest) => apiRequest<AdminBrand>("/admin/brands", json("POST", body)),
    update: (id: string, body: SaveBrandRequest) => apiRequest<AdminBrand>(`/admin/brands/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/brands/${id}`, { method: "DELETE" }),
  },
  categories: {
    list: () => apiRequest<AdminCategory[]>("/admin/categories"),
    create: (body: SaveCategoryRequest) => apiRequest<AdminCategory>("/admin/categories", json("POST", body)),
    update: (id: string, body: SaveCategoryRequest) => apiRequest<AdminCategory>(`/admin/categories/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/categories/${id}`, { method: "DELETE" }),
  },
  coupons: {
    list: () => apiRequest<AdminCoupon[]>("/admin/coupons"),
    create: (body: SaveCouponRequest) => apiRequest<{ id: string }>("/admin/coupons", json("POST", body)),
    update: (id: string, body: SaveCouponRequest) => apiRequest<void>(`/admin/coupons/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/coupons/${id}`, { method: "DELETE" }),
  },
  shipping: {
    list: () => apiRequest<AdminShippingMethod[]>("/admin/shipping-methods"),
    create: (body: SaveShippingMethodRequest) => apiRequest<{ id: string }>("/admin/shipping-methods", json("POST", body)),
    update: (id: string, body: SaveShippingMethodRequest) => apiRequest<void>(`/admin/shipping-methods/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/shipping-methods/${id}`, { method: "DELETE" }),
  },
  orders: {
    list: (params: { status?: OrderStatus | ""; paymentStatus?: string; shippingMethodId?: string; search?: string; minTotal?: number; maxTotal?: number; from?: string; to?: string; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<OrderListItem>>(`/admin/orders${queryString(params)}`),
    summary: () => apiRequest<AdminOrderSummary>("/admin/orders/summary"),
    detail: (id: string) => apiRequest<OrderDetail>(`/admin/orders/${id}`),
    updateStatus: (id: string, status: OrderStatus, trackingCode?: string) =>
      apiRequest<OrderDetail>(`/admin/orders/${id}/status`, json("PUT", { status, trackingCode: trackingCode || null })),
  },
  reviews: {
    list: (params: { status?: ReviewStatus | ""; rating?: number | ""; productId?: string; from?: string; to?: string; search?: string; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<AdminReview>>(`/admin/reviews${queryString(params)}`),
    summary: () => apiRequest<AdminReviewSummary>("/admin/reviews/summary"),
    moderate: (id: string, status: ReviewStatus) => apiRequest<void>(`/admin/reviews/${id}/moderation`, json("PUT", { status })),
    remove: (id: string) => apiRequest<void>(`/admin/reviews/${id}`, { method: "DELETE" }),
  },
  blog: {
    list: (params: { search?: string; status?: BlogPostStatus | ""; category?: string; from?: string; to?: string; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<AdminBlogPost>>(`/admin/blog${queryString(params)}`),
    summary: () => apiRequest<AdminBlogSummary>("/admin/blog/summary"),
    detail: (id: string) => apiRequest<BlogPostDetail>(`/admin/blog/${id}`),
    create: (body: SaveBlogPostRequest) => apiRequest<{ id: string }>("/admin/blog", json("POST", body)),
    update: (id: string, body: SaveBlogPostRequest) => apiRequest<void>(`/admin/blog/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/blog/${id}`, { method: "DELETE" }),
  },
  faqs: {
    list: () => apiRequest<AdminFaq[]>("/admin/faqs"),
    create: (body: SaveFaqRequest) => apiRequest<{ id: string }>("/admin/faqs", json("POST", body)),
    update: (id: string, body: SaveFaqRequest) => apiRequest<void>(`/admin/faqs/${id}`, json("PUT", body)),
    remove: (id: string) => apiRequest<void>(`/admin/faqs/${id}`, { method: "DELETE" }),
  },
  contacts: {
    list: (params: { status?: ContactStatus | ""; from?: string; to?: string; search?: string; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<AdminContactMessage>>(`/admin/contact-messages${queryString(params)}`),
    summary: () => apiRequest<AdminContactSummary>("/admin/contact-messages/summary"),
    update: (id: string, status: ContactStatus, adminNote?: string) =>
      apiRequest<void>(`/admin/contact-messages/${id}`, json("PUT", { status, adminNote: adminNote || null })),
    remove: (id: string) => apiRequest<void>(`/admin/contact-messages/${id}`, { method: "DELETE" }),
  },
  account: {
    profile: () => apiRequest<AccountProfile>("/account/profile"),
    updateProfile: (body: { firstName: string; lastName: string; email?: string | null; birthDate?: string | null; gender?: string | null; avatarUrl?: string | null }) => apiRequest<AccountProfile>("/account/profile", json("PUT", body)),
    changePassword: (body: { currentPassword: string; newPassword: string; confirmPassword: string }) => apiRequest<void>("/account/change-password", json("POST", body)),
    twoFactorStatus: () => apiRequest<TwoFactorStatus>("/account/two-factor"),
    twoFactorSetup: () => apiRequest<TwoFactorSetup>("/account/two-factor/setup", { method: "POST" }),
    twoFactorEnable: (code: string) => apiRequest<TwoFactorEnabled>("/account/two-factor/enable", json("POST", { code })),
    twoFactorSmsRequest: () => apiRequest<void>("/account/two-factor/sms/request", { method: "POST" }),
    twoFactorSmsEnable: (code: string) => apiRequest<TwoFactorEnabled>("/account/two-factor/sms/enable", json("POST", { code })),
    twoFactorDisable: (code: string) => apiRequest<void>("/account/two-factor/disable", json("POST", { code })),
    sessions: () => apiRequest<AccountSession[]>("/account/sessions"),
    revokeSession: (id: string) => apiRequest<void>(`/account/sessions/${id}`, { method: "DELETE" }),
    revokeOtherSessions: () => apiRequest<void>("/account/sessions/revoke-others", { method: "POST" }),
  },
  users: {
    list: (params: { search?: string; role?: string; isActive?: boolean | ""; sort?: string; page?: number; pageSize?: number }) =>
      apiRequest<PagedResult<AdminUserListItem>>(`/admin/users${queryString(params)}`),
    detail: (id: string) => apiRequest<AdminUserDetail>(`/admin/users/${id}`),
    create: (body: CreateAdminUserRequest) => apiRequest<{ id: string }>("/admin/users", json("POST", body)),
    update: (id: string, body: UpdateAdminUserRequest) => apiRequest<void>(`/admin/users/${id}`, json("PUT", body)),
    setStatus: (id: string, isActive: boolean) => apiRequest<void>(`/admin/users/${id}/status`, json("PATCH", { isActive })),
    setPassword: (id: string, newPassword: string) => apiRequest<void>(`/admin/users/${id}/password`, json("POST", { newPassword })),
    revokeSessions: (id: string) => apiRequest<void>(`/admin/users/${id}/revoke-sessions`, { method: "POST" }),
  },
  access: {
    overview: () => apiRequest<AdminAccessOverview>("/admin/access/overview"),
    updateRolePermissions: (roleName: string, permissions: string[]) => apiRequest<void>(`/admin/access/roles/${encodeURIComponent(roleName)}/permissions`, json("PUT", { permissions })),
    staff: (params: { search?: string; role?: string; isActive?: boolean | ""; page?: number; pageSize?: number }) => apiRequest<PagedResult<AdminStaffListItem>>(`/admin/access/staff${queryString(params)}`),
    createStaff: (body: CreateAdminStaffRequest) => apiRequest<{ id: string }>("/admin/access/staff", json("POST", body)),
    updateStaff: (id: string, body: UpdateAdminStaffRequest) => apiRequest<void>(`/admin/access/staff/${id}`, json("PUT", body)),
    setStaffStatus: (id: string, isActive: boolean) => apiRequest<void>(`/admin/access/staff/${id}/status`, json("PATCH", { isActive })),
    setStaffPassword: (id: string, newPassword: string) => apiRequest<void>(`/admin/access/staff/${id}/password`, json("POST", { newPassword })),
    revokeStaffSessions: (id: string) => apiRequest<void>(`/admin/access/staff/${id}/revoke-sessions`, { method: "POST" }),
  },
  uploads: {
    image: async (file: File, signal?: AbortSignal) => {
      const form = new FormData();
      form.append("file", file);
      return apiRequest<{ url: string }>("/uploads/images", { method: "POST", body: form, signal });
    },
    video: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiRequest<{ url: string }>("/uploads/videos", { method: "POST", body: form });
    },
  },
};
